import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface GeoBlockProps {
  children: React.ReactNode;
}

const GeoBlock: React.FC<GeoBlockProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAllowed, setIsAllowed] = useState(false);

  useEffect(() => {
    const checkLocation = async () => {
      try {
        // Skip geo-blocking for localhost development
        if (
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1"
        ) {
          setIsAllowed(true);
          setIsChecking(false);
          return;
        }

        // Try multiple geolocation services as fallbacks
        let geoData = null;

        // First try: ipapi.co (free, reliable)
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);

          const response = await fetch("https://ipapi.co/json/", {
            signal: controller.signal,
          });
          clearTimeout(timeoutId);

          if (response.ok) {
            geoData = await response.json();
          }
        } catch (error) {
          // ipapi.co failed, trying fallback
        }

        // Fallback: ip-api.com (free, no rate limits)
        if (!geoData) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);

            const response = await fetch("https://ip-api.com/json/", {
              signal: controller.signal,
            });
            clearTimeout(timeoutId);

            if (response.ok) {
              geoData = await response.json();
            }
          } catch (error) {
            // ip-api.com failed, trying final fallback
          }
        }

        // Final fallback: ipinfo.io (free tier)
        if (!geoData) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);

            const response = await fetch("https://ipinfo.io/json", {
              signal: controller.signal,
            });
            clearTimeout(timeoutId);

            if (response.ok) {
              geoData = await response.json();
            }
          } catch (error) {
            // All geolocation services failed
          }
        }

        if (geoData) {
          // Check if the country code is US (handle different response formats)
          const countryCode =
            geoData.country_code || geoData.country || geoData.countryCode;
          const countryName = geoData.country_name || geoData.country || "";

          // More flexible US detection
          const isUS =
            countryCode === "US" ||
            countryCode === "USA" ||
            countryName === "United States" ||
            countryName === "United States of America" ||
            countryCode === "United States";

          if (isUS) {
            setIsAllowed(true);
          } else {
            // Redirect to home page if not from US
            navigate("/", { replace: true });
          }
        } else {
          // If we can't determine location, allow access (fail open for better UX)
          setIsAllowed(true);
        }
      } catch (error) {
        console.error("Error checking location:", error);
        // If we can't determine location, allow access (fail open)
        setIsAllowed(true);
      } finally {
        setIsChecking(false);
      }
    };

    checkLocation();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Verifying location...</p>
        </div>
      </div>
    );
  }

  if (!isAllowed) {
    return null; // Will redirect to home page
  }

  return <>{children}</>;
};

export default GeoBlock;
